/**
 * @architecture-header 1
 * @layer module
 * @domain notifications
 * @responsibility Optimistically mark inbox items read and persist real notification rows.
 * @state-owner TanStack Query cache
 * @allowed-imports app environment; same module query; contracts; TanStack Query
 * @forbidden routes; app bootstrap; direct Supabase client; localStorage
 * @public-api via modules/notifications/index.ts
 * @side-effects authenticated notification update outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InboxItem } from '@vispo/contracts';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { inboxKeys } from './inbox.queries';

export function useMarkInboxRead(activeContextId: string) {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (item: InboxItem) => {
      if (environment.demoMode || !item.id.startsWith('notif-')) return;
      const notificationId = item.id.slice('notif-'.length);
      await environment.apiClient.request(`/api/inbox/${encodeURIComponent(notificationId)}/read`, {
        method: 'PUT',
      });
    },
    onMutate: async (item) => {
      const queryKey = inboxKeys.list(activeContextId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<InboxItem[]>(queryKey);
      queryClient.setQueryData<InboxItem[]>(queryKey, (items = []) =>
        items.map((current) =>
          current.id === item.id ? { ...current, isRead: true, isCompleted: true } : current,
        ),
      );
      return { previous };
    },
    onError: (_error, _item, context) => {
      if (context?.previous) {
        queryClient.setQueryData(inboxKeys.list(activeContextId), context.previous);
      }
    },
  });
}
